/* HTTP client for the customer PWA.
 *
 * Plan invariants enforced here:
 *   1. External API path is tenant-prefixed: `/api/{tenantCode}/v1/...`.
 *   2. Every mutation MUST carry `Idempotency-Key`. We auto-generate one
 *      with `crypto.randomUUID()` if the caller does not provide one, so
 *      duplicate webhook/server retries can dedupe deterministically.
 *   3. G14/G17 one-credential XOR: either `Authorization: Bearer …`
 *      **or** `credentials: "include"` cookie-session path — never both.
 *      Cookie Spec A = credentials jar only (zero Authorization).
 *      Dual transitional = Bearer identity uses credentials omit.
 *      HttpOnly refresh cookies still ride the cookie path (or dedicated
 *      refresh fetch with CSRF). CSRF double-submit (`X-Requested-With`)
 *      attaches on Yes-matrix mutations via customerAuthCsrf markers.
 *   4. Response envelope is always `{ success, data | error }` — we unwrap
 *      data for callers and rethrow `ApiError` on failure.
 */

import { getRetryAfterMs } from 'pi-kiosk-shared';
import { readRequestId, setClientCorrelationId } from 'pi-kiosk-shared/logging';
import { setSentryCorrelationId } from 'pi-kiosk-shared/sentry';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import i18n from '../../i18n.js';
import { resolveApiErrorFromHttpResponse } from '../http/resolveApiErrorFromHttpResponse.js';
import { captureCustomerRateLimitBreadcrumb } from '../observability/sentry.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import {
  customerAuthCsrfHeaders,
  shouldAttachCustomerAuthCsrf} from '../session/customerAuthCsrf.js';
import { resolveCustomerCredentialSend } from '../session/resolveCustomerCredentialSend.js';
import { resolveTenantApiUrl } from './resolveApiUrl.js';
import { apiClientLog } from './logging.js';
import {
  buildRequestFailureCacheKey,
  CachedRequestFailureError,
  clearAllRequestFailures,
  clearRequestFailure,
  getCachedFailureStatus,
  markRequestFailure,
  shouldSkipCachedFailure} from './requestFailureCache.js';
import {
  getHttpStormRetryAfterMs,
  HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS,
  isHttpStormLatched} from '../http/httpStormDetectedLatch.js';
import {
  CustomerOutboundFairQueueOverflowError,
  enqueueCustomerOutbound,
  normalizeCustomerOutboundSourceKey} from '../http/customerOutboundFairQueue.js';
import {
  HttpStormLatchedError,
  stormAwareFetch} from '../http/stormAwareFetch.js';
import {
  shouldAttemptSessionRefreshOn401,
} from '../session/sessionRefresh.js';
import {
  fetchPlatformMe,
  MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE,
} from '../session/fetchPlatformMe.js';
import { resolvePublicApiFailureMessage } from '../errors/publicApiFailureMessage.js';

/** Thrown when browse tenant is platform chrome and no home jwt is available. */
export const PLATFORM_CHROME_NO_API_TENANT = 'PLATFORM_CHROME_NO_API_TENANT';

/** G7 — getConditional must not transport identity `/me`. */
export const ME_CONDITIONAL_FORBIDDEN = 'ME_CONDITIONAL_FORBIDDEN';

function isCustomerAccountMePath(path: string): boolean {
  const bare = path.split('?')[0] ?? path;
  return (
    bare === '/customer-account/me' ||
    bare.endsWith('/customer-account/me')
  );
}

/**
 * Handoff resolve is token-scoped via `?h=` but GET failure cache keys omit query.
 * One 404 (wrong/stale token, Strict Mode race) must not TTL-block later resolves.
 */
function isCheckoutHandoffResolvePath(path: string): boolean {
  const bare = path.split('?')[0] ?? path;
  return (
    bare === '/customer-checkout/handoff/resolve' ||
    bare.endsWith('/customer-checkout/handoff/resolve')
  );
}

/**
 * G6 — map {@link fetchPlatformMe} into ApiClient success / ApiError
 * (parity with `fetchPlatformAccountMe`).
 * G2 — skipQueue: `/me` does **not** take an outer scheduleOutbound slot (see
 * {@link ApiClient.request}); coordinator owns single-flight + optional enqueue.
 * skipQueue lets ApiClient-as-leader wire without nested fair-queue on the same
 * auth-hot source key; peer-as-leader (enqueued) is join-only — no hold-and-wait.
 */
async function fetchMeViaPlatformCoordinator(
  apiBaseUrl: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const result = await fetchPlatformMe({ apiBaseUrl }, signal, { skipQueue: true });
  switch (result.kind) {
    case 'ok':
      return result.me;
    case 'missing_cred':
      // Sentinel message kept for isMissingCredentialUnauthorized string match;
      // UI must use code/status or toCustomerFacingErrorMessage (not raw .message).
      throw new ApiError(
        MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE,
        401,
        'UNAUTHORIZED',
      );
    case 'unauthorized':
      throw new ApiError(
        resolvePublicApiFailureMessage({ status: 401, code: 'UNAUTHORIZED' }),
        401,
        'UNAUTHORIZED',
      );
    case 'ceiling':
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 429,
          code: 'ME_ATTEMPT_CEILING',
        }),
        429,
        'ME_ATTEMPT_CEILING',
      );
    case 'transient':
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 503,
          code: 'ME_TRANSIENT',
        }),
        503,
        'ME_TRANSIENT',
      );
    default: {
      const _exhaustive: never = result;
      return _exhaustive;
    }
  }
}

/** G17 — re-export sessionRefresh SSOT deny list for ApiClient consumers. */
export {
  CUSTOMER_REFRESH_401_DENY_SUBSTRINGS as CUSTOMER_API_401_REFRESH_DENY_SUBSTRINGS,
  shouldAttemptSessionRefreshOn401 as shouldAttemptCustomerApiRefreshOn401,
} from '../session/sessionRefresh.js';

/**
 * G15 — establish / login twin: drop poisoned GET failure TTL so post-auth
 * preference sync is not blocked by a pre-auth 401 race.
 * (Customer has no admin-style deny cache yet — failure cache is the SoT here.)
 */
export function clearCustomerApiAuthCaches(): void {
  clearAllRequestFailures();
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly data: unknown;
  public readonly retryAfterMs: number | undefined;

  constructor(
    message: string,
    status: number,
    code?: string,
    data?: unknown,
    retryAfterMs?: number
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.retryAfterMs = retryAfterMs;
  }
}

export interface ApiSuccessEnvelope<T> {
  success: true;
  data: T;
}

export interface ApiErrorDetails {
  /**
   * Package F — explain-only axis denials. Preserve alongside existing `code`.
   * Prefer reading via {@link parseEntitlementDenialsFromApiErrorData}.
   */
  entitlementDenials?: ReadonlyArray<{ blockKey: string; kind: string }>;
  [key: string]: unknown;
}

export interface ApiErrorEnvelope {
  success: false;
  error: string | { code?: string; message?: string; details?: ApiErrorDetails };
  code?: string;
  errors?: Array<{ path?: string; message: string }>;
  /** Structured diagnostics — kept on ApiError.data; never strip at parse. */
  details?: ApiErrorDetails;
}

type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiErrorEnvelope;

export type UnauthorizedHandler = (
  path: string,
  code: string | undefined,
  message?: string
) => Promise<'retry' | 'suspend' | 'end' | 'none'>;

export interface ApiClientOptions {
  config: RuntimeConfig;
  getToken: () => string | null;
  getTenantCode: () => string;
  onUnauthorized?: UnauthorizedHandler;
  /**
   * T4/U3: when omitted, defaults to true (attach Bearer if getToken non-empty).
   * U4: AppDataProvider passes `() => !` so R1 omits Bearer.
   * G14 Cookie Spec A: pass `() => false` (or empty getToken) → credentials jar only.
   */
  shouldAttachCustomerBearer?: () => boolean;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  idempotencyKey?: string;
  /**
   * Mutations default to auto-generated `Idempotency-Key`.
   * `optional` — send the header only when `idempotencyKey` is set
   * (POST /customer-checkout/refund-request only).
   */
  idempotency?: 'auto' | 'optional';
  query?: Record<string, string | number | boolean | undefined>;
  /** Optional extra headers (e.g. analytics session auth token). */
  headers?: Record<string, string>;
  /**
   * Override browse/provider tenant for this request (e.g. consent promote
   * immediately after `writeJwtTenantCode` before React remounts providers).
   */
  tenantCode?: string;
  /**
   * Fetch cache mode (e.g. memberships branding — `no-store` parity with
   * public customer-tenants).
   */
  cache?: RequestCache;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function throwIfHttpStormLatched(): void {
  if (!isHttpStormLatched()) {
    return;
  }
  const retryAfterMs =
    getHttpStormRetryAfterMs() || HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS;
  throw new ApiError(
    resolvePublicApiFailureMessage({
      status: 429,
      code: 'HTTP_STORM_DETECTED',
    }),
    429,
    'HTTP_STORM_DETECTED',
    undefined,
    retryAfterMs,
  );
}

function rethrowHttpStormLatchedAsApiError(err: unknown): never {
  if (err instanceof HttpStormLatchedError) {
    throw new ApiError(
      resolvePublicApiFailureMessage({
        status: 429,
        code: 'HTTP_STORM_DETECTED',
        rawMessage: err.message,
      }),
      429,
      'HTTP_STORM_DETECTED',
      undefined,
      err.retryAfterMs,
    );
  }
  throw err;
}

/**
 * G14+G17 — exactly one credential identity per request.
 * Spec A / `attachBearer:false` → force cookie jar (zero Authorization).
 * Dual transitional → Bearer XOR via {@link resolveCustomerCredentialSend}.
 */
function resolveCredentialSend(args: {
  attachBearer: boolean;
  token: string | null;
}): {
  authorizationHeaders: Record<string, string>;
  credentials: RequestCredentials;
} {
  const send = resolveCustomerCredentialSend({
    // Spec A cookie-first: never jar-drop when attach is false (even if memory JWT exists).
    bearerToken: args.attachBearer ? args.token : null});
  return {
    authorizationHeaders:
      send.authorizationHeader !== undefined
        ? { Authorization: send.authorizationHeader }
        : {},
    credentials: send.credentials};
}

/**
 * G3 — merge caller opts.headers without dual-send.
 * Cookie credentials identity (`include`) never accepts Authorization override;
 * Spec A / attachBearer=false XOR SoT stays jar-only.
 */
function mergeOptsHeaders(
  headers: Record<string, string>,
  optsHeaders: Record<string, string> | undefined,
  credentials: RequestCredentials,
): void {
  if (optsHeaders === undefined) {
    return;
  }
  for (const [key, value] of Object.entries(optsHeaders)) {
    if (value.length === 0) {
      continue;
    }
    if (
      credentials === 'include' &&
      key.toLowerCase() === 'authorization'
    ) {
      continue;
    }
    headers[key] = value;
  }
}

export class ApiClient {
  private readonly options: ApiClientOptions;

  constructor(options: ApiClientOptions) {
    this.options = options;
  }

  /**
   * Fair-queue entry (admin twin: `scheduleAdminOutbound`).
   * Storm latch freezes starts; `run` (requestInternal / getConditionalInternal) executes in-slot.
   */
  private scheduleOutbound<T>(
    method: string,
    path: string,
    signal: AbortSignal | undefined,
    run: () => Promise<T>,
    onStart?: () => void,
  ): Promise<T> {
    normalizeCustomerOutboundSourceKey(method, path);
    return enqueueCustomerOutbound({ method, path, signal, onStart, run });
  }

  private mapOutboundScheduleError(err: unknown): never {
    if (err instanceof CustomerOutboundFairQueueOverflowError) {
      // Overflow Error.message stays technical for logs; ApiError.message is public.
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 429,
          code: err.code,
          rawMessage: err.message,
        }),
        429,
        err.code,
        undefined,
        err.retryAfterMs,
      );
    }
    throw err;
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method ?? 'GET';
    // G2 — identity `/me` bypasses outer fair-queue: fetchPlatformMe already
    // enqueues (CSP/boot) or uses skipQueue (this path). scheduleOutbound around
    // the coordinator caused hold-and-wait (ApiClient sourceInflight + peer
    // waiter on GET:/customer-account/me) or dual-wire after peer release.
    if (method === 'GET' && isCustomerAccountMePath(path)) {
      return (await fetchMeViaPlatformCoordinator(
        this.options.config.apiBaseUrl,
        opts.signal,
      )) as T;
    }
    try {
      return await this.scheduleOutbound(method, path, opts.signal, () =>
        this.requestInternal(path, opts, false),
      );
    } catch (err) {
      this.mapOutboundScheduleError(err);
    }
  }

  /**
   * GET with conditional caching (G-12 / G-45 catalog ETag poll).
   * G9 — same onUnauthorized recovery + retry-once as requestInternal.
   */
  async getConditional<T>(
    path: string,
    opts: RequestOptions & { ifNoneMatch?: string } = {},
  ): Promise<{ status: 200 | 304; data?: T; etag?: string | null }> {
    try {
      return await this.scheduleOutbound('GET', path, opts.signal, () =>
        this.getConditionalInternal(path, opts, false),
      );
    } catch (err) {
      this.mapOutboundScheduleError(err);
    }
  }

  /**
   * Unqueued network work. Callers must wrap via {@link scheduleOutbound}
   * so fair-queue timing applies; 401 retry stays inside the slot.
   */
  private async getConditionalInternal<T>(
    path: string,
    opts: RequestOptions & { ifNoneMatch?: string },
    retried: boolean,
  ): Promise<{ status: 200 | 304; data?: T; etag?: string | null }> {
    const { config, getToken, getTenantCode, onUnauthorized, shouldAttachCustomerBearer } =
      this.options;
    throwIfHttpStormLatched();

    // G7 — hard-ban identity `/me` on conditional GET (use request / fetchPlatformMe).
    if (isCustomerAccountMePath(path)) {
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 0,
          code: ME_CONDITIONAL_FORBIDDEN,
        }),
        0,
        ME_CONDITIONAL_FORBIDDEN,
      );
    }

    const tenantCode = resolveRequestApiTenantCode(getTenantCode(), opts.tenantCode);
    const url = resolveTenantApiUrl(config.apiBaseUrl, tenantCode, path, opts.query);

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
    };
    const attachBearer = (shouldAttachCustomerBearer ?? (() => true))();
    const { authorizationHeaders, credentials } = resolveCredentialSend({
      attachBearer,
      token: getToken()});
    Object.assign(headers, authorizationHeaders);
    if (opts.ifNoneMatch) {
      headers['If-None-Match'] = opts.ifNoneMatch;
    }
    mergeOptsHeaders(headers, opts.headers, credentials);

    let res: Response;
    try {
      res = await stormAwareFetch(
        url,
        {
          method: 'GET',
          headers,
          signal: opts.signal,
          credentials,
          ...(opts.cache !== undefined ? { cache: opts.cache } : {}),
        },
        { skipQueue: true },
      );
    } catch (err) {
      rethrowHttpStormLatchedAsApiError(err);
    }

    const getId = readRequestId(res);
    if (getId !== undefined) {
      setClientCorrelationId(getId);
      setSentryCorrelationId(getId);
    }

    const etag = res.headers.get('etag');
    if (res.status === 304) {
      return { status: 304, etag };
    }

    if (!res.ok) {
      let envelope: ApiEnvelope<T> | null = null;
      try {
        envelope = (await res.json()) as ApiEnvelope<T>;
      } catch {
        envelope = null;
      }
      const bodyRequestIdCandidate =
        envelope !== null
          ? (envelope as unknown as { requestId?: unknown }).requestId
          : undefined;
      const bodyRequestId =
        typeof bodyRequestIdCandidate === 'string' ? bodyRequestIdCandidate : undefined;
      const correlationId = getId ?? bodyRequestId;
      if (correlationId !== undefined) {
        setClientCorrelationId(correlationId);
        setSentryCorrelationId(correlationId);
      }
      const resolved = resolveApiErrorFromHttpResponse({
        response: res,
        body: envelope,
      });
      const code = resolved.code;
      const message = resolved.message;
      logCustomerApiFailure('Customer API GET failed', {
        status: res.status,
        correlationId,
        errorCode: code});
      // Spec A dying-access: missing_cred still invokes onUnauthorized so
      // handleUnauthorizedForPath can rotate when the established-session latch
      // is set (anonymous missing_cred returns 'none' there — BAN skip here).
      if (
        !retried &&
        res.status === 401 &&
        onUnauthorized !== undefined &&
        shouldAttemptSessionRefreshOn401(path)
      ) {
        const action = await onUnauthorized(path, code, message);
        if (action === 'retry') {
          return this.getConditionalInternal(path, opts, true);
        }
      }
      throw new ApiError(message, res.status, code, envelope, resolved.retryAfterMs);
    }

    if (res.status === 204) {
      return { status: 200, etag };
    }

    const envelope = (await res.json()) as ApiEnvelope<T>;
    if (!envelope.success || envelope.data === undefined) {
      const failCode =
        envelope.success === false ? envelope.code : 'MISSING_DATA';
      const raw =
        envelope.success === false ? envelope.error : undefined;
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: res.status,
          code: failCode,
          rawMessage: typeof raw === 'string' ? raw : undefined,
        }),
        res.status,
        failCode,
        envelope,
      );
    }

    return { status: 200, data: envelope.data, etag };
  }

  /**
   * Unqueued network work. Callers must wrap via {@link scheduleOutbound}
   * so fair-queue timing applies; 401 retry stays inside the slot.
   */
  private async requestInternal<T>(
    path: string,
    opts: RequestOptions,
    retried: boolean
  ): Promise<T> {
    const { config, getToken, getTenantCode, onUnauthorized, shouldAttachCustomerBearer } =
      this.options;
    throwIfHttpStormLatched();
    const method = opts.method ?? 'GET';

    // G6/G2 — safety net: request() routes /me before scheduleOutbound; if this
    // path is ever reached, still join the coordinator (never a second wire lane).
    if (method === 'GET' && isCustomerAccountMePath(path)) {
      return (await fetchMeViaPlatformCoordinator(
        config.apiBaseUrl,
        opts.signal,
      )) as T;
    }

    const tenantCode = resolveRequestApiTenantCode(getTenantCode(), opts.tenantCode);

    const url = resolveTenantApiUrl(config.apiBaseUrl, tenantCode, path, opts.query);
    const cacheKey = buildRequestFailureCacheKey([method, tenantCode, path]);
    const skipGetFailureCache = isCheckoutHandoffResolvePath(path);
    // Skip cache only on the first attempt — an in-flight 401→refresh→retry must
    // not be blocked by a failure marked earlier in this same call stack.
    // Handoff resolve: never short-circuit (token is in query, not in cache key).
    if (
      !retried &&
      method === 'GET' &&
      !skipGetFailureCache &&
      shouldSkipCachedFailure(cacheKey)
    ) {
      const cachedStatus = getCachedFailureStatus(cacheKey) ?? 0;
      throw new CachedRequestFailureError(cacheKey, cachedStatus);
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
      'Content-Type': 'application/json'};

    const attachBearer = (shouldAttachCustomerBearer ?? (() => true))();
    const { authorizationHeaders, credentials } = resolveCredentialSend({
      attachBearer,
      token: getToken()});
    Object.assign(headers, authorizationHeaders);

    if (MUTATION_METHODS.has(method)) {
      if (opts.idempotency === 'optional') {
        if (opts.idempotencyKey !== undefined && opts.idempotencyKey.trim().length > 0) {
          headers['Idempotency-Key'] = opts.idempotencyKey.trim();
        }
      } else {
        headers['Idempotency-Key'] = opts.idempotencyKey ?? generateIdempotencyKey();
      }
    }

    if (shouldAttachCustomerAuthCsrf(method, path)) {
      Object.assign(headers, customerAuthCsrfHeaders());
    }

    mergeOptsHeaders(headers, opts.headers, credentials);

    let res: Response;
    try {
      res = await stormAwareFetch(
        url,
        {
          method,
          headers,
          body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
          signal: opts.signal,
          credentials,
          ...(opts.cache !== undefined ? { cache: opts.cache } : {}),
        },
        { skipQueue: true },
      );
    } catch (err) {
      if (err instanceof CachedRequestFailureError) {
        throw err;
      }
      if (err instanceof HttpStormLatchedError) {
        throw new ApiError(
          resolvePublicApiFailureMessage({
            status: 429,
            code: 'HTTP_STORM_DETECTED',
            rawMessage: err.message,
          }),
          429,
          'HTTP_STORM_DETECTED',
          undefined,
          err.retryAfterMs,
        );
      }
      // Network blips are transient — do not TTL-block identical GETs for ~5 minutes.
      // Soft mount / Account Retry must still be able to reach the network.
      // Log path/method only — never put fetch TypeError.message on ApiError (UI).
      apiClientLog.error('Customer API network failure', { method, path });
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 0,
          code: 'NETWORK_ERROR',
          rawMessage: err instanceof Error ? err.message : undefined,
        }),
        0,
        'NETWORK_ERROR'
      );
    }

    const mutationId = readRequestId(res);
    if (mutationId !== undefined) {
      setClientCorrelationId(mutationId);
      setSentryCorrelationId(mutationId);
    }

    let envelope: ApiEnvelope<T> | null = null;
    if (res.status !== 204) {
      try {
        envelope = (await res.json()) as ApiEnvelope<T>;
      } catch {
        envelope = null;
      }
    }

    if (!res.ok || envelope === null || envelope.success === false) {
      const bodyRequestIdCandidate =
        envelope !== null
          ? (envelope as unknown as { requestId?: unknown }).requestId
          : undefined;
      const bodyRequestId =
        typeof bodyRequestIdCandidate === 'string' ? bodyRequestIdCandidate : undefined;
      const correlationId = mutationId ?? bodyRequestId;
      if (correlationId !== undefined) {
        setClientCorrelationId(correlationId);
        setSentryCorrelationId(correlationId);
      }
      const resolved = resolveApiErrorFromHttpResponse({
        response: res,
        body: envelope,
      });
      const code = resolved.code;
      logCustomerApiFailure('Customer API request failed', {
        method,
        path,
        status: res.status,
        correlationId,
        errorCode: code});
      if (res.status === 429) {
        if (!skipGetFailureCache) {
          markRequestFailure(cacheKey, res.status);
        }
        const retryAfterMs = resolved.retryAfterMs ?? getRetryAfterMs({ response: res, data: envelope });
        captureCustomerRateLimitBreadcrumb({
          path,
          method,
          retryAfterMs});
        throw new ApiError(resolved.message, res.status, code, envelope, retryAfterMs);
      }
      if (res.status === 401) {
        const softMessage = resolved.message;
        // Spec A dying-access: missing_cred still invokes onUnauthorized so
        // handleUnauthorizedForPath can rotate when the established-session latch
        // is set (anonymous missing_cred returns 'none' there — BAN skip here).
        if (
          !retried &&
          onUnauthorized !== undefined &&
          shouldAttemptSessionRefreshOn401(path)
        ) {
          const action = await onUnauthorized(path, code, softMessage);
          if (action === 'retry') {
            // Auth recovered — do not poison the GET failure cache from this 401.
            clearRequestFailure(cacheKey);
            return this.requestInternal(path, opts, true);
          }
        }
        // Soft recovery ('none' / suspend / end), denylist, or no handler:
        // auth races, cooldown, and membership gates are transient — never TTL-block the GET.
        // G15: `/me` 401 must not poison failure cache.
        clearRequestFailure(cacheKey);
        throw new ApiError(softMessage, res.status, code, envelope);
      }
      if (res.status === 403) {
        // w0-storm-csrf403 — CSRF_FAILED / 403 never triggers refresh (401-only recover).
        clearRequestFailure(cacheKey);
        const softMessage = resolved.message;
        throw new ApiError(softMessage, res.status, code, envelope);
      }
      // Non-auth failures (5xx, 422, …) may short-circuit identical GETs for TTL.
      // Handoff resolve excluded — see isCheckoutHandoffResolvePath.
      if (!skipGetFailureCache) {
        markRequestFailure(cacheKey, res.status);
      }
      throw new ApiError(resolved.message, res.status, code, envelope, resolved.retryAfterMs);
    }

    if (envelope.data === undefined) {
      if (!skipGetFailureCache) {
        markRequestFailure(cacheKey, res.status);
      }
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: res.status,
          code: 'MISSING_DATA',
        }),
        res.status,
        'MISSING_DATA',
        envelope,
      );
    }

    clearRequestFailure(cacheKey);
    return envelope.data;
  }

  /**
   * GET raw text/HTML via tenant-scoped URL (same path + credential rules as {@link request}).
   * Used for seller store-policy documents (text/html, not JSON envelope).
   */
  async requestText(path: string, opts: RequestOptions = {}): Promise<string> {
    const method = opts.method ?? 'GET';
    if (method !== 'GET') {
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 0,
          code: 'INVALID_METHOD',
          rawMessage: 'requestText supports GET only',
        }),
        0,
        'INVALID_METHOD',
      );
    }
    try {
      return await this.scheduleOutbound(method, path, opts.signal, () =>
        this.requestTextInternal(path, opts, false),
      );
    } catch (err) {
      this.mapOutboundScheduleError(err);
    }
  }

  private async requestTextInternal(
    path: string,
    opts: RequestOptions,
    retried: boolean,
  ): Promise<string> {
    const { config, getToken, getTenantCode, onUnauthorized, shouldAttachCustomerBearer } =
      this.options;
    throwIfHttpStormLatched();
    const tenantCode = resolveRequestApiTenantCode(getTenantCode(), opts.tenantCode);
    const url = resolveTenantApiUrl(config.apiBaseUrl, tenantCode, path, opts.query);
    const headers: Record<string, string> = {
      Accept: 'text/html, text/plain;q=0.9, */*;q=0.8',
      'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
    };
    const attachBearer = (shouldAttachCustomerBearer ?? (() => true))();
    const { authorizationHeaders, credentials } = resolveCredentialSend({
      attachBearer,
      token: getToken(),
    });
    Object.assign(headers, authorizationHeaders);
    mergeOptsHeaders(headers, opts.headers, credentials);

    let res: Response;
    try {
      res = await stormAwareFetch(
        url,
        {
          method: 'GET',
          headers,
          signal: opts.signal,
          credentials,
          ...(opts.cache !== undefined ? { cache: opts.cache } : {}),
        },
        { skipQueue: true },
      );
    } catch (err) {
      if (err instanceof HttpStormLatchedError) {
        rethrowHttpStormLatchedAsApiError(err);
      }
      apiClientLog.error('Customer API text network failure', { method: 'GET', path });
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: 0,
          code: 'NETWORK_ERROR',
          rawMessage: err instanceof Error ? err.message : undefined,
        }),
        0,
        'NETWORK_ERROR',
      );
    }

    if (!res.ok) {
      if (
        res.status === 401 &&
        !retried &&
        onUnauthorized !== undefined &&
        shouldAttemptSessionRefreshOn401(path)
      ) {
        const action = await onUnauthorized(path, 'UNAUTHORIZED');
        if (action === 'retry') {
          return this.requestTextInternal(path, opts, true);
        }
      }
      throw new ApiError(
        resolvePublicApiFailureMessage({
          status: res.status,
          code: res.status === 404 ? 'NOT_FOUND' : 'HTTP_ERROR',
        }),
        res.status,
        res.status === 404 ? 'NOT_FOUND' : 'HTTP_ERROR',
      );
    }

    return res.text();
  }
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * G10 — dying-session 401 UNAUTHORIZED is expected after soft refresh / expiry;
 * keep true failures at error level.
 */
function logCustomerApiFailure(
  message: string,
  fields: {
    status: number;
    correlationId?: string;
    errorCode?: string;
    method?: string;
    path?: string;
  },
): void {
  const dyingSessionUnauthorized =
    fields.status === 401 && fields.errorCode === 'UNAUTHORIZED';
  if (dyingSessionUnauthorized) {
    apiClientLog.warn(message, fields);
    return;
  }
  apiClientLog.error(message, fields);
}

/**
 * Never build `/api/__platform__/v1/...`. Prefer explicit override, then
 * remapped home jwt under platform chrome.
 */
function resolveRequestApiTenantCode(
  browseTenantCode: string,
  overrideTenantCode: string | undefined,
): string {
  const override =
    overrideTenantCode !== undefined ? overrideTenantCode.trim() : '';
  const candidate = override.length > 0 ? override : browseTenantCode.trim();
  if (candidate.length === 0 || isPlatformChromeTenant(candidate)) {
    throw new ApiError(
      i18n.t('errors.openShopToContinue', { ns: 'customer' }),
      0,
      PLATFORM_CHROME_NO_API_TENANT,
    );
  }
  return candidate;
}
